import React, { useEffect } from 'react'

import { useEditAlarm } from '../hooks/useEditAlarm'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import * as Sentry from '@sentry/nextjs'
import { SubmitHandler, useForm } from 'react-hook-form'

import { Box, GridItem } from '@chakra-ui/react'

import {
  Button,
  FormInputControl,
  FormInputSelectControl,
  SkeletonModalBody,
} from '@/components/ui'

import { EditDeviceModalSection } from '../../components/EditDeviceModalSection'
import { FourColumnGrid } from '../../components/FourColumnGrid'
import { SectionHeader } from '../../components/SectionHeader'
import { TwoColumnGrid } from '../../components/TwoColumnGrid'
import { AlarmDataTypeI } from '../../types/types'
import { isPlaced } from '../../utils/utils'
import { getAlarmInputs } from '../utils/utils'

interface EditAlarmFormIProps {
  deviceId: string
  onLoadingChange: (loading: boolean) => void
}

export const EditAlarmForm = ({
  deviceId,
  onLoadingChange,
}: EditAlarmFormIProps) => {
  const { shouldEnableUpdateDevice } = usePermissions()
  const {
    alarmData,
    isLoading,
    isUpdateDeviceLoading,
    isUpdateAlarmLoading,
    floorsOptions,
    updateAlarm,
  } = useEditAlarm({
    deviceId,
  })
  const form = useForm<AlarmDataTypeI>({
    mode: 'onChange',
  })
  const {
    handleSubmit,
    errors,
    register,
    control,
    setValue,
    reset,
    formState: { dirtyFields, isDirty },
  } = form
  const { showSuccess, showError } = useToasts()

  /**
   * Handles form submission for alarm updates
   * Prepares device and alarm data, then calls the update function
   */
  const onSubmit: SubmitHandler<AlarmDataTypeI> = async (values) => {
    const formData = {
      id: deviceId,
      name: values.name,
      floorId: values.location?.value,
    }
    const alarmInputData = getAlarmInputs(deviceId, values)

    try {
      await updateAlarm(formData, alarmInputData, dirtyFields)
      showSuccess('Alarm updated.')
    } catch (e) {
      Sentry.captureException(e)
      showError()
    } finally {
      reset(values)
    }
  }

  useEffect(() => {
    if (!isLoading) {
      setValue('name', alarmData?.device?.name, { shouldValidate: true })
      setValue(
        'location',
        floorsOptions?.find((i) => i.value === alarmData?.device?.floor?.id),
        {
          shouldValidate: true,
        }
      )

      setValue('externalId', alarmData?.device?.alarm?.externalId, {
        shouldValidate: false,
      })
    }
  }, [isLoading, floorsOptions, alarmData, setValue])

  useEffect(() => {
    // Update parent component's loading state
    onLoadingChange(isLoading)
  }, [alarmData, isLoading])

  return (
    <Box>
      {!alarmData || isLoading ? (
        <SkeletonModalBody />
      ) : (
        <EditDeviceModalSection mb={6}>
          <TwoColumnGrid>
            <GridItem>
              <SectionHeader
                subTitle='Configure the basic device information and connections settings for this device '
                title='Device Details & Connection'
              />
            </GridItem>
            {/* @ts-ignore */}
            <GridItem justifySelf='end'>
              <Button
                isDisabled={!shouldEnableUpdateDevice || !isDirty}
                isLoading={isUpdateDeviceLoading || isUpdateAlarmLoading}
                loadingText='Saving Changes'
                onClick={handleSubmit(onSubmit)}
              >
                Save
              </Button>
            </GridItem>
          </TwoColumnGrid>

          <form>
            <FourColumnGrid>
              <GridItem>
                <FormInputControl
                  data-testid='devicesPage_editAlarmModal_name'
                  errorMessage={errors.name && errors.name.message}
                  id='name'
                  inputRef={register({
                    required: 'Device Name is required',
                  })}
                  isInvalid={!!errors.name}
                  label='Device Name'
                  placeholder='Enter the Device Name'
                />
              </GridItem>
              <GridItem>
                <FormInputSelectControl
                  control={control}
                  data-testid='devicesPage_editAlarmModal_location'
                  defaultValue={null}
                  errorMessage='Select a device location'
                  id='location'
                  isClearable
                  isDisabled={isPlaced(alarmData?.device)}
                  isInvalid={!!errors?.location}
                  label='Device Location'
                  options={floorsOptions}
                  placeholder='Select Device Location'
                  rules={{ required: true }}
                  tooltipText={
                    isPlaced(alarmData?.device) &&
                    'Device must not be placed to update device location'
                  }
                />
              </GridItem>
              <GridItem>
                <FormInputControl
                  data-testid='devicesPage_editAlarmModal_externalId'
                  id='externalId'
                  inputRef={register()}
                  label='External ID'
                  placeholder='Alarm External ID'
                />
              </GridItem>
            </FourColumnGrid>
          </form>
        </EditDeviceModalSection>
      )}
    </Box>
  )
}
